import { getContacts } from '../store/selectors/contact.selector';
import { CATEGORY_TYPES, SEARCH_TYPES_KEYS } from '../constants';
import { ANALYTICS_CATEGORIES_TYPES } from '../constants/analytics';
import { getCategoryType } from './Utils';

const FILTER_KEY_TO_API = {
  SPECIALTY: 'specialty',
  SUBSPECIALTY: 'subspecialty',
  CONTENT_TYPE: 'contentType',
  TARGET_AUDIENCE: 'targetAudience',
  LABELS: 'labels',
};

// SearchType tab → API `filters.type` (EntityType[]). The tab narrows the query
// server-side. `none` is absent → no type filter (all entities). Media maps to
// knowledgeItems for now; the document/media thumbnail split needs backend work.
const SEARCH_TYPE_TO_API_TYPES = {
  [SEARCH_TYPES_KEYS.documents]: ['knowledgeItems'],
  [SEARCH_TYPES_KEYS.media]: ['knowledgeItems'],
  [SEARCH_TYPES_KEYS.contacts]: ['contact', 'users'],
  [SEARCH_TYPES_KEYS.compendium]: ['labValues'],
  [SEARCH_TYPES_KEYS.admission]: ['admissions'],
};

export const mapFiltersToApi = (filters = {}) => {
  const apiFilters = {};
  Object.entries(FILTER_KEY_TO_API).forEach(([reduxKey, apiKey]) => {
    const set = filters[reduxKey];
    if (set instanceof Set && set.size > 0) {
      apiFilters[apiKey] = Array.from(set);
    }
  });

  // The active SearchType tab rides inside `filters` as its raw value; map it to
  // the API `type` array here, right before the request. `none` → no type filter.
  const typeFilter = SEARCH_TYPE_TO_API_TYPES[filters.searchType];
  if (typeFilter) apiFilters.type = typeFilter;

  return apiFilters;
};

const facetsToTagsMap = (facets = {}) => ({
  specialty: facets.specialty || {},
  subspecialty: facets.subspecialty || {},
  contentType: facets.contentType || {},
  targetAudience: facets.targetAudience || {},
  labels: facets.labels || {},
});

// Knowledge hits carry their own row (`hit.item`).
const LOCAL_LIST_BY_TYPE = {
  knowledgeCategories: state => state.knowledge?.categories,
  labValues: state => state.lab?.items,
  labCategories: state => state.lab?.categories,
  admissions: state => state.admission?.categories,
  contact: state => getContacts(state),
  users: state => getContacts(state),
};

// The row a knowledge hit falls back to when the backend could not look its LIST row
// up: built from the search-index fields the hit itself carries. No thumbnail, item
// type, sync provider or attachment count — those live only on the row.
const rowFromHit = hit => ({
  id: hit.id,
  workspaceId: hit.workspaceId,
  ownerIds: hit.ownerIds,
  title: hit.title,
  subtitle: hit.subtitle,
  link: hit.url,
  tags: hit.tags,
  labels: hit.labels,
  keywords: hit.keywords,
  expiresAt: hit.expiresAt,
});

// `hit.item` is the LIST row; `null` means the item is gone or hidden from the caller
// (dropped); the key is absent when the backend's row lookup failed and the search
// still answered (kept, from the hit's own fields).
const hitItem = (hit, state) => {
  if (hit.type !== 'knowledgeItems') {
    return LOCAL_LIST_BY_TYPE[hit.type]?.(state)?.find(x => x.id === hit.id);
  }
  return hit.item === undefined ? rowFromHit(hit) : hit.item;
};

const hydrate = (hits = [], state) =>
  hits
    .map(hit => {
      const item = hitItem(hit, state);
      return item ? { item, type: hit.type, score: hit.score } : null;
    })
    .filter(Boolean);

export const emptyBucket = () => ({ results: [], extended: [], total: 0 });

// The API is migrating buckets from `SearchHit[]` to `{ results, extended, total }`,
// where `extended` holds hits that only matched by tolerating a typo. Accept both
// shapes so backend and client can deploy independently.
// `total` is carried through deliberately but intentionally unused by the client —
// it exists for backend-internal verification. Nothing renders it; see `countTier`.
const normalizeBucket = bucket =>
  Array.isArray(bucket)
    ? { results: bucket, extended: [], total: bucket.length }
    : {
        results: bucket?.results || [],
        extended: bucket?.extended || [],
        total: bucket?.total ?? 0,
      };

const hydrateBucket = (bucket, state) => {
  const { results, extended, total } = normalizeBucket(bucket);
  return { results: hydrate(results, state), extended: hydrate(extended, state), total };
};

// `groupBy: 'workspace'` response → { workspaces: { [wsId]: bucket }, contacts, tagsMap },
// where each bucket is `{ results, extended, total }`. The synthetic `contact` bucket
// (users + contacts merged, per SEARCH_API.md) is lifted to its own field; the `null`
// bucket (no workspace) is dropped. Each hit keeps its API `type` so the renderer
// dispatches on it directly.
export const assembleResultsFromApiGroups = ({ groups, facets, count = 0, state }) => {
  const buckets = groups || {};
  const workspaces = {};
  Object.entries(buckets).forEach(([key, bucket]) => {
    if (key === 'contact' || key === 'null') return;
    workspaces[key] = hydrateBucket(bucket, state);
  });

  return {
    workspaces,
    contacts: hydrateBucket(buckets.contact, state),
    tagsMap: facetsToTagsMap(facets),
    count,
  };
};

// Counts come from the hydrated tiers, never from the API's `total` — `hydrate`
// drops any hit it cannot resolve, so `total` overstates what renders.
// `results` is null until the first search resolves, so guard rather than
// defaulting the parameter — a default only covers undefined.
const countTier = (tier, results) => {
  const { workspaces = {}, contacts } = results || {};
  return Object.values(workspaces).reduce(
    (sum, bucket) => sum + (bucket?.[tier]?.length || 0),
    contacts?.[tier]?.length || 0,
  );
};

export const getExtendedCount = results => countTier('extended', results);

// Workspace hits by score, extended after primary, deduped by type and id
// (a linked item hits once per workspace). No contacts.
const rankWorkspaceHits = results => {
  const buckets = Object.values(results?.workspaces || {});
  const tierByScoreDesc = tier =>
    buckets.flatMap(bucket => bucket[tier]).sort((a, b) => (b.score ?? 0) - (a.score ?? 0));

  const seen = new Set();
  return [...tierByScoreDesc('results'), ...tierByScoreDesc('extended')].filter(
    ({ type, item }) => {
      const key = `${type}:${item.id}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    },
  );
};

/**
 * Knowledge-item hits in display order, for the link editor.
 *
 * @param {object|null|undefined} results - Assembled search results (`useSearch` data).
 * @returns {Array<{ item: object, type: string, score: number }>} Knowledge-item hits, best first.
 * @example
 * const hits = getKnowledgeItemHits(results);
 */
export const getKnowledgeItemHits = results =>
  rankWorkspaceHits(results).filter(({ type }) => type === 'knowledgeItems');

const isRootPath = path => path === 'root' || path === 'ROOT';

// A category without a path cannot be placed, so it is left out with the sections.
const isRootOrSection = item =>
  !item.path ||
  isRootPath(item.path) ||
  getCategoryType(item) === ANALYTICS_CATEGORIES_TYPES.SECTION;

// API type → picker row type. Types missing here cannot be stored as resources.
const PICKER_TYPE_BY_API_TYPE = {
  knowledgeItems: () => SEARCH_TYPES_KEYS.documents,
  knowledgeCategories: () => SEARCH_TYPES_KEYS.category,
  labCategories: () => SEARCH_TYPES_KEYS.category,
  labValues: () => SEARCH_TYPES_KEYS.compendium,
  admissions: item =>
    item.type === CATEGORY_TYPES.CHECKS ? SEARCH_TYPES_KEYS.admission : SEARCH_TYPES_KEYS.category,
};

/**
 * Hits a resource picker can store, in display order, typed as picker rows.
 * No root categories or sections.
 *
 * @param {object|null|undefined} results - Assembled search results (`useSearch` data).
 * @returns {Array<{ item: object, type: string, score: number }>} Pickable hits, best first.
 * @example
 * const hits = getPickerHits(results);
 * hits.map(({ type, item }) => renderByType[type]({ item, type }));
 */
export const getPickerHits = results =>
  rankWorkspaceHits(results)
    .map(hit => {
      const type = PICKER_TYPE_BY_API_TYPE[hit.type]?.(hit.item);
      return type ? { ...hit, type } : null;
    })
    .filter(Boolean)
    .filter(({ type, item }) => type !== SEARCH_TYPES_KEYS.category || !isRootOrSection(item));

export const emptyResultsShape = () => ({
  workspaces: {},
  contacts: emptyBucket(),
  tagsMap: {
    specialty: {},
    subspecialty: {},
    contentType: {},
    targetAudience: {},
    labels: {},
  },
  count: 0,
});
